import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  Form,
  Typography,
  NumberInput,
  TextAreaInput,
  ButtonsWrapper,
  Button,
  SelectInput,
  TYPOGRAPHY_SIZE,
  PLATFORM_THEME_COLORS,
  ICONS,
  DeleteModal,
  PopoverTooltip,
  Icon,
} from '@thinknet-gmbh/platform-common-ui';

import { ROUTES } from 'src/components/app/app-routes.constants';
import { SETTINGS_PAGES } from 'src/pages/settings/settings.constants';
import { useDeleteConsultantNumber } from 'src/hooks/consultant-numbers/delete-consultant-number.hook';

import { EditDatevWrapper, DeleteButton } from './edit-datev-form.styles';

const EditDatevForm = ({
  data,
  onFinishHandler,
  isConsultantNumber,
}: {
  data: any;
  onFinishHandler: (values: any) => void;
  isConsultantNumber?: boolean;
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isOpenedDeleteModal, setIsOpenedDeleteModal] = useState(false);
  const { deleteConsultantNumber } = useDeleteConsultantNumber({
    onSuccess: () => navigate(`${ROUTES.settings}?type=${SETTINGS_PAGES.datev}`),
  });

  const onSubmitDelete = () => {
    deleteConsultantNumber({
      variables: {
        consultantNumberId: data._id,
      },
    });
  };

  return (
    <EditDatevWrapper>
      <DeleteModal
        translate={t}
        title={t('settings:datev.deleteConsultantNumber.title')}
        subTitle={t('settings:datev.deleteConsultantNumber.subtitle', {
          number: data.consultantNumber,
        })}
        onClose={() => setIsOpenedDeleteModal(false)}
        onSubmit={onSubmitDelete}
        isOpened={isOpenedDeleteModal}
      />
      <Typography weight="bold" size={TYPOGRAPHY_SIZE.medium}>
        {`${t(
          `settings:datev.labels.${isConsultantNumber ? 'consultantNumber' : 'clientNumber'}`
        )} №${isConsultantNumber ? data.consultantNumber : data.datevNumber}`}
      </Typography>
      <Form
        layout="vertical"
        onFinish={onFinishHandler}
        initialValues={{
          ...data,
          datevNumber: isConsultantNumber ? data.consultantNumber : data.datevNumber,
        }}
      >
        <Form.Item
          name="datevNumber"
          label={
            isConsultantNumber
              ? t('settings:datev.labels.consultantNumber')
              : t('settings:datev.labels.clientNumber')
          }
          rules={[{ required: true, message: t('settings:datev.validation.clientNumberRequired') }]}
        >
          <NumberInput isNumericString format="№#######" placeholder="№0000000" />
        </Form.Item>
        <Form.Item name="details" label={t('employee:labels.details')}>
          <TextAreaInput showCount maxLength={30} placeholder={t('employee:labels.writeDetails')} />
        </Form.Item>
        {!isConsultantNumber && (
          <Form.Item label={t('common:labels.status')} rules={[{ required: true }]}>
            <SelectInput disabled placeholder={data.status.toLowerCase()}></SelectInput>
          </Form.Item>
        )}
        <ButtonsWrapper marginTop={isConsultantNumber ? 35 : 16}>
          <Button htmlType="submit" type="primary">
            {t('common:buttons.save')}
          </Button>
          <Button onClick={() => navigate(`${ROUTES.settings}?type=${SETTINGS_PAGES.datev}`)}>
            {t('common:buttons.cancel')}
          </Button>

          {isConsultantNumber && (
            <PopoverTooltip
              placement="right"
              overlayInnerStyle={{ width: '334px' }}
              trigger={data.datevClientNumbers.length > 0 ? 'hover' : ''}
              overlay={
                <Typography fontColor={PLATFORM_THEME_COLORS.primary.white}>
                  {t('settings:datev.labels.popupOverlayText')}
                </Typography>
              }
            >
              <DeleteButton
                onClick={() =>
                  !(data.datevClientNumbers.length > 0) && setIsOpenedDeleteModal(true)
                }
                isDisabled={data.datevClientNumbers.length > 0}
              >
                <Icon icon={ICONS.trash} color={PLATFORM_THEME_COLORS.tone.red} size={15} />
                <Typography fontColor={PLATFORM_THEME_COLORS.tone.red}>
                  {t('settings:datev.buttons.deleteNumber')}
                </Typography>
              </DeleteButton>
            </PopoverTooltip>
          )}
        </ButtonsWrapper>
      </Form>
    </EditDatevWrapper>
  );
};

export default EditDatevForm;
